/**
 * react-query hooks over `repository.ts`.
 *
 * Why react-query and not an MST store: the same per-document cache feeds two
 * mount points (the list panel and the viewer rail) plus every row badge, and
 * `useRealtimeInvalidate` already bridges `postgres_changes` into react-query
 * with coalescing and re-join recovery. The one piece of global state — the
 * unread dot — lives in `MentionsStore`.
 *
 * Cache entries hold the RAW rows (the canonical shape); consumers derive with
 * `useMemo`. Two hooks on one key must agree on the cached shape.
 */

import { useMemo } from "react";
import { useQuery, type QueryFilters } from "@tanstack/react-query";

import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeTopic } from "@/lib/realtime/channel";
import { getIssueActivity } from "@/stores/pm/repository";
import { mapActivity, mapThreads, sortThreads } from "./mappers";
import {
  fetchCounts,
  fetchMentionAudience,
  fetchThreadActivity,
  fetchThreadScope,
  fetchVersionGroupDocuments,
  listThreads,
  lookupThread,
  lookupThreadForIssue,
} from "./repository";
import type {
  DocumentCommentCounts,
  DocumentCommentScope,
  DocumentThread,
  DocumentThreadLookup,
  DocumentVersionRef,
  MentionAudienceUser,
  ThreadRow,
} from "./types";
import { CommentsUnavailableError } from "./types";

/** Threads and counts are fed by realtime; the TTL is the way back when a
 *  socket drops silently (realtime is never a source of truth). */
const LIVE_STALE_MS = 60 * 1000;

export const documentCommentKeys = {
  scope: (documentId: number) => ["document-comment-scope", documentId] as const,
  threadsForBuilding: (buildingId: number) => ["document-comment-threads", buildingId] as const,
  threads: (buildingId: number, groupKey: string) =>
    ["document-comment-threads", buildingId, groupKey] as const,
  countsForBuilding: (buildingId: number) => ["document-comment-counts", buildingId] as const,
  counts: (buildingId: number, idsKey: string) =>
    ["document-comment-counts", buildingId, idsKey] as const,
  activity: (threadId: number) => ["document-comment-thread-activity", threadId] as const,
  lookup: (threadId: number) => ["document-comment-thread-lookup", threadId] as const,
  audience: (documentId: number) => ["document-comment-audience", documentId] as const,
  originThread: (issueId: number) => ["document-comment-origin", issueId] as const,
  issueActivity: (issueId: number) => ["document-comment-issue-activity", issueId] as const,
};

/** The lineage key a scope's threads are cached under. */
export function threadGroupKey(scope: DocumentCommentScope): string {
  return scope.versionGroup ?? `doc:${scope.id}`;
}

export function isCommentsUnavailable(error: unknown): boolean {
  return error instanceof CommentsUnavailableError;
}

// ---------------------------------------------------------------------------
// Scope
// ---------------------------------------------------------------------------

export function useThreadScopeQuery(documentId: number | null) {
  return useQuery({
    queryKey: documentCommentKeys.scope(documentId ?? 0),
    queryFn: () => fetchThreadScope(documentId as number),
    enabled: documentId != null,
  });
}

// ---------------------------------------------------------------------------
// Threads across the version group
// ---------------------------------------------------------------------------

export interface ThreadsCacheEntry {
  versions: DocumentVersionRef[];
  rows: ThreadRow[];
}

export interface DocumentThreadsResult {
  threads: DocumentThread[];
  open: DocumentThread[];
  resolved: DocumentThread[];
  versions: DocumentVersionRef[];
  isLoading: boolean;
  isError: boolean;
  unavailable: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

export function useDocumentThreadsQuery(
  scope: DocumentCommentScope | null,
  currentUserId: number | null
): DocumentThreadsResult {
  const query = useQuery({
    queryKey: scope
      ? documentCommentKeys.threads(scope.buildingId, threadGroupKey(scope))
      : ["document-comment-threads", 0, "none"],
    queryFn: async (): Promise<ThreadsCacheEntry> => {
      const versions = await fetchVersionGroupDocuments(scope as DocumentCommentScope);
      const rows = await listThreads(versions.map((v) => v.id));
      return { versions, rows };
    },
    enabled: scope != null,
    staleTime: LIVE_STALE_MS,
    // A thread's completion can change from the Issues page (the sub-issue's
    // status mirrors onto it in the database); a fresh mount of the panel or
    // rail must show that even when its cache entry is not yet stale.
    refetchOnMount: "always",
    retry: (count, error) => !(error instanceof CommentsUnavailableError) && count < 1,
  });

  const rows = query.data?.rows;
  const threads = useMemo(
    () => (rows ? sortThreads(mapThreads(rows, currentUserId)) : []),
    [rows, currentUserId]
  );
  const open = useMemo(() => threads.filter((t) => t.resolvedAt === null), [threads]);
  const resolved = useMemo(() => threads.filter((t) => t.resolvedAt !== null), [threads]);

  return {
    threads,
    open,
    resolved,
    versions: query.data?.versions ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    unavailable: isCommentsUnavailable(query.error),
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// Row badges
// ---------------------------------------------------------------------------

/**
 * Open/completed counts for the ids a view is rendering. The key carries the
 * sorted id list so a scrolled-in page is its own entry, and the previous
 * entry is kept while the next loads so badges do not flash.
 */
export function useDocumentCommentCountsQuery(
  buildingId: number | null,
  documentIds: number[],
  enabled = true
): Map<number, DocumentCommentCounts> {
  const sorted = useMemo(
    () =>
      Array.from(new Set(documentIds.filter((id) => Number.isFinite(id)))).sort((a, b) => a - b),
    [documentIds]
  );
  const idsKey = sorted.join(",");
  const query = useQuery({
    queryKey: documentCommentKeys.counts(buildingId ?? 0, idsKey),
    queryFn: () => fetchCounts(sorted),
    enabled: enabled && buildingId != null && sorted.length > 0,
    staleTime: LIVE_STALE_MS,
    placeholderData: (previous) => previous,
    retry: false,
  });
  return useMemo(() => {
    const out = new Map<number, DocumentCommentCounts>();
    for (const [id, row] of query.data ?? []) {
      out.set(id, { open: row.open_count, resolved: row.resolved_count });
    }
    return out;
  }, [query.data]);
}

// ---------------------------------------------------------------------------
// History, lookups, audience
// ---------------------------------------------------------------------------

export function useThreadActivityQuery(threadId: number | null, enabled: boolean) {
  const query = useQuery({
    queryKey: documentCommentKeys.activity(threadId ?? 0),
    queryFn: () => fetchThreadActivity(threadId as number),
    enabled: enabled && threadId != null,
    staleTime: LIVE_STALE_MS,
  });
  const data = query.data;
  const activity = useMemo(() => (data ? mapActivity(data) : []), [data]);
  return { activity, isLoading: query.isLoading, isError: query.isError };
}

/** An issue-backed thread's history is its sub-issue's activity log. */
export function useIssueActivityQuery(issueId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: documentCommentKeys.issueActivity(issueId ?? 0),
    queryFn: () => getIssueActivity(issueId as number),
    enabled: enabled && issueId != null,
    staleTime: LIVE_STALE_MS,
  });
}

function toLookup(row: Awaited<ReturnType<typeof lookupThread>>): DocumentThreadLookup | null {
  if (!row || row.deleted_at !== null) return null;
  return {
    id: row.id,
    documentId: row.document_id,
    buildingId: row.building_id,
    pageNumber: row.page_number,
    resolved: row.resolved_at !== null,
  };
}

export function useThreadLookupQuery(threadId: number | null) {
  return useQuery({
    queryKey: documentCommentKeys.lookup(threadId ?? 0),
    queryFn: async () => toLookup(await lookupThread(threadId as number)),
    enabled: threadId != null,
  });
}

/** The thread an issue was filed from — drives the "Origin" line on an issue. */
export function useIssueOriginThreadQuery(issueId: number | null) {
  return useQuery({
    queryKey: documentCommentKeys.originThread(issueId ?? 0),
    queryFn: async () => toLookup(await lookupThreadForIssue(issueId as number)),
    enabled: issueId != null,
    retry: false,
  });
}

export function useMentionAudienceQuery(documentId: number | null): MentionAudienceUser[] {
  const query = useQuery({
    queryKey: documentCommentKeys.audience(documentId ?? 0),
    queryFn: () => fetchMentionAudience(documentId as number),
    enabled: documentId != null,
    retry: false,
  });
  return query.data ?? [];
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

export interface DocumentCommentsRealtimeOptions {
  enabled?: boolean;
  /** For state outside react-query — the unread dot in `MentionsStore`. */
  onChange?: () => void;
}

/**
 * One building-scoped channel for every comments consumer on a page. Every
 * table filters on `building_id` (realtime takes one `eq` per binding), and
 * every event is a bare signal: no `payload.old`, so the caches refetch.
 *
 * `pm_issue_comments` carries no building column, so an issue-backed thread's
 * new or edited comments reach us through `pm_issue_activity` (the comment
 * triggers log there with the building), and its status through `pm_issues`.
 */
export function useDocumentCommentsRealtime(
  buildingId: number | null,
  options: DocumentCommentsRealtimeOptions = {}
): void {
  const { enabled = true, onChange } = options;
  const id = buildingId ?? 0;
  const invalidate: QueryFilters[] = useMemo(
    () => [
      { queryKey: documentCommentKeys.threadsForBuilding(id) },
      { queryKey: documentCommentKeys.countsForBuilding(id) },
      { queryKey: ["document-comment-thread-activity"] },
      { queryKey: ["document-comment-issue-activity"] },
      { queryKey: ["document-comment-thread-lookup"] },
      { queryKey: ["document-comment-origin"] },
    ],
    [id]
  );
  useRealtimeInvalidate({
    enabled: enabled && buildingId != null,
    topic: realtimeTopic("document_comments", id),
    bindings: [
      { event: "*", table: "document_comment_threads", filter: `building_id=eq.${id}` },
      { event: "*", table: "document_comments", filter: `building_id=eq.${id}` },
      { event: "*", table: "pm_issue_activity", filter: `building_id=eq.${id}` },
      { event: "*", table: "pm_issues", filter: `building_id=eq.${id}` },
    ],
    invalidate,
    onChange,
    flushOnJoin: true,
  });
}
